import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { AlertTriangle, ChevronLeft, ChevronRight, Lock, Milestone, X } from 'lucide-react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { EtainablSpinner } from '@/components/etainabl-spinner'
import { useTrackerPresentation } from '@/queries/tracker'
import { useIsAdmin } from '@/lib/auth'
import {
  trackerStatusLabels,
  trackerStatusVariants,
  devStageLabels,
  devStageVariants,
  priorityLabels,
} from '@/lib/status'
import type { TrackerPresentationEntry } from '@/types/tracker'

dayjs.extend(relativeTime)

const INTERACTIVE_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'])

// Arrow/Escape must not hijack typing inside a form control — there are none
// on this read-only deck today, but the guard costs nothing and the moment
// one is added (or a future toast/dialog stacks a button on top) a stray
// Escape or arrow shouldn't double as both "activate this control" and
// "navigate the deck".
function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return INTERACTIVE_TAGS.has(target.tagName) || target.isContentEditable
}

function shortId(entry: TrackerPresentationEntry) {
  return `#${entry.shortId.toUpperCase()}`
}

// Full-screen, chrome-less message for the guard/empty/error states — the
// deck can't fall back on AppShell's centering, so each of these owns its
// own h-screen frame instead.
function MessageScreen({
  icon,
  title,
  body,
  onExit,
}: {
  icon: React.ReactNode
  title: string
  body: string
  onExit: () => void
}) {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background">
      <EmptyState icon={icon} title={title} body={body} />
      <Button variant="outline" onClick={onExit}>
        Exit
      </Button>
    </div>
  )
}

function MessagePreview({ message }: { message: TrackerPresentationEntry['recentMessages'][number] }) {
  return (
    <div className="rounded border border-border bg-muted/40 p-3">
      <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{message.author?.name ?? 'Unknown'}</span>
        <span aria-hidden>·</span>
        <span>{dayjs(message.createdAt).fromNow()}</span>
      </div>
      <p className="whitespace-pre-wrap text-sm text-foreground">{message.body}</p>
    </div>
  )
}

function PresentationCard({ entry }: { entry: TrackerPresentationEntry }) {
  const { tracker } = entry
  return (
    <div className="space-y-6">
      <div>
        <span className="text-xs text-muted-foreground tabular-nums">{shortId(entry)}</span>
        <h2 className="text-2xl font-semibold text-foreground">{entry.subject}</h2>
      </div>

      {/* Dev-stage badge only — no raw linearIssueState (arbitrary,
          engineer-edited workspace text) and no clickable Linear link: this
          is a read-only deck on a shared screen, not a one-click hop into
          the internal workspace mid-call. Both stay on the detail view. */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={trackerStatusVariants[tracker.status]}>{trackerStatusLabels[tracker.status]}</Badge>
        <Badge variant="outline">{priorityLabels[entry.priority]} priority</Badge>
        {tracker.category && <Badge variant="outline">{tracker.category}</Badge>}
        <Badge variant={devStageVariants[tracker.devStage]}>{devStageLabels[tracker.devStage]}</Badge>
      </div>

      {tracker.impact && (
        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Impact</h3>
          <p className="text-sm text-foreground">{tracker.impact}</p>
        </div>
      )}

      {entry.recentMessages.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Recent activity</h3>
          {entry.recentMessages.map(m => (
            <MessagePreview key={m._id} message={m} />
          ))}
        </div>
      )}
    </div>
  )
}

// Full-screen deck for a client call: the sidebar/nav/view-as controls are
// structurally absent while this route is mounted (see __root.tsx), and this
// component itself makes exactly one request — GET .../tracker/presentation —
// and renders only what it returned. It must never reach for ticket detail or
// a message thread of its own: that per-card fetch is exactly how the
// predecessor app leaked an internal note onto a shared screen.
export function PresentationView({ companyId, asUser }: { companyId?: string; asUser?: string }) {
  const navigate = useNavigate()
  const isAdmin = useIsAdmin()
  const [includeClosed, setIncludeClosed] = useState(false)
  // Tracked by ticket id, not array position: a background refetch
  // (refetchOnWindowFocus) can reorder the list mid-call, and an index would
  // silently swap the ticket under the presenter's feet. findIndex below
  // re-locates the same id after any reorder; -1 (id no longer present, or
  // no id chosen yet) falls back to the first card.
  const [currentId, setCurrentId] = useState<string | undefined>(undefined)

  // Present is staff-only — clients don't present. Fold that into the query
  // itself (not just the render below) so a client who lands on this URL
  // triggers no request at all, not even one the server would just ignore.
  const { data, isLoading, error } = useTrackerPresentation(
    isAdmin ? companyId : undefined,
    { includeClosed },
    isAdmin ? asUser : undefined,
  )

  const tickets = data?.data ?? []
  const total = data?.total ?? tickets.length
  const rawIndex = tickets.findIndex(t => t._id === currentId)
  const clampedIndex = rawIndex === -1 ? 0 : rawIndex
  const current = tickets[clampedIndex]

  const exit = () => navigate({ to: '/tracker', search: { asUser } as never })

  function goTo(nextIndex: number) {
    const clamped = Math.max(0, Math.min(nextIndex, tickets.length - 1))
    const target = tickets[clamped]
    if (target) setCurrentId(target._id)
  }

  // Toggling the filter changes which tickets exist at all — never leave the
  // deck parked on an id the new page doesn't have; falls back to the first
  // card via the -1 branch above.
  useEffect(() => {
    setCurrentId(undefined)
  }, [includeClosed])

  // A toast already on screen when the presenter navigates here (e.g. they
  // just edited a tracker field) would otherwise ride along — see __root.tsx
  // for why dropping <Toaster/> from this branch isn't sufficient on its own
  // for every path. Belt and braces: clear the queue the moment the deck
  // mounts too.
  useEffect(() => {
    toast.dismiss()
  }, [])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (isInteractiveTarget(e.target)) return
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        goTo(clampedIndex + 1)
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goTo(clampedIndex - 1)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        exit()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, clampedIndex, asUser])

  // Staff-only, checked here (not just by whoever links to this route) so
  // presenting is verifiably unreachable for a client regardless of how they
  // arrived — mirrors TrackerControls/TrackerListView's isAdmin gate.
  if (!isAdmin) {
    return (
      <MessageScreen
        icon={<Lock />}
        title="Presentation is available to Etainabl staff"
        body="This view is for staff to walk a client through their tracker on a call."
        onExit={exit}
      />
    )
  }

  // Reachable only for an admin: arrived at /tracker/present with neither
  // companyId nor an asUser lens, so the query never ran (see `enabled`
  // above) — a distinct, honest message rather than the generic "couldn't
  // load" below, which implies a request was attempted and failed.
  if (!companyId && !asUser) {
    return (
      <MessageScreen
        icon={<Milestone />}
        title="Nothing selected to present"
        body="Open Present from a specific company's row on the tracker list."
        onExit={exit}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <EtainablSpinner size="lg" srLabel="Loading presentation" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <MessageScreen
        icon={<AlertTriangle />}
        title="Couldn't load the tracker"
        body="Something went wrong loading the presentation."
        onExit={exit}
      />
    )
  }

  return (
    <div className="flex h-screen flex-col bg-background">
      <header className="flex items-center justify-between border-b border-border px-6 py-4">
        <div className="flex items-baseline gap-3 min-w-0">
          <h1 className="truncate text-lg font-semibold text-foreground">{data.companyName}</h1>
          <span className="shrink-0 text-sm text-muted-foreground tabular-nums">
            {tickets.length === 0 ? '0 of 0' : `${clampedIndex + 1} of ${total}`}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={includeClosed}
              onChange={e => setIncludeClosed(e.target.checked)}
            />
            Include closed
          </label>
          <Button variant="outline" size="sm" onClick={exit}>
            <X className="mr-1.5 h-4 w-4" />
            Exit
          </Button>
        </div>
      </header>

      {tickets.length === 0 || !current ? (
        <div className="flex flex-1 items-center justify-center">
          <EmptyState
            icon={<Milestone />}
            title="Nothing to present"
            body="No tracked issues to present yet."
          />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 items-center justify-between gap-6 px-10 py-8">
          <Button
            variant="ghost"
            size="icon-lg"
            aria-label="Previous"
            disabled={clampedIndex === 0}
            onClick={() => goTo(clampedIndex - 1)}
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>

          <div className="min-w-0 max-w-3xl flex-1 overflow-y-auto">
            <PresentationCard entry={current} />
          </div>

          <Button
            variant="ghost"
            size="icon-lg"
            aria-label="Next"
            disabled={clampedIndex >= tickets.length - 1}
            onClick={() => goTo(clampedIndex + 1)}
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </div>
      )}
    </div>
  )
}
